import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, OutlinedInput, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useRef, useState } from "react";
import { ReactComponent as Stage } from "../../../assets/icons/stage.svg";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";
import ConfirmDialog from "../ConfirmDialog";
import { produce } from 'immer';

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 500,
            height: 'fit-content',
            minHeight: '30vh',
            maxHeight: '70vh',
            padding: '16px 24px',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.3rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 0',
        height: '100%',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingTop: '16px',
    },
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    list: {
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    input: {
        height: 'fit-content',
        borderRadius: 8,
        border: `1px solid ${theme.colors.neutral['400']}`,
        '&::placeholder': {
            fontSize: '14px',
            lineHeight: '21.7px',
            fontWeight: 400,
            color: theme.colors.neutral['600']
        }
    },
    icon: {
        padding: 0,
        color: theme.colors.neutral['500'],
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    cancelBtn: {
        height: '40px',
        width: '88px',
        padding: '8px 24px',
        borderRadius: '40px',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    addStage: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        color: theme.colors.primary['500'],
    },
    saveStage: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: theme.colors.primary['500'],
        textTransform: 'none',
        minWidth: '0 !important',
        padding: '0 !important',
        '&:hover': {
            background: '#fff !important',
        }
    },
    badge: {
        display: 'flex',
        width: 'auto',
        height: '30px',
        padding: '4px 24px',
        borderRadius: '32px',
        backgroundColor: theme.colors.primary[100],
        color: theme.colors.primary[600],
        alignItems: 'center',
        justifyContent: 'center',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 600,
    },
}));

const ManageStagesModal: React.FC<{
    open: boolean,
    onClose: () => void,
}> = ({ open, onClose }) => {
    const classes = useStyles();
    const { stages, setStages } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);
    const [stage, setStage] = useState<string>('');
    const [editStage, setEditStage] = useState<string|null>(null);
    const [newStage, setNewStage] = useState<string>('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deletableStage, setDeletableStage] = useState('');
    const stageRef = useRef<string[]>(structuredClone(stages));

    const isExistingStage = useCallback((edited: string) =>
        !!stages.find(stage => stage.trim().toLowerCase() === edited.trim().toLowerCase())
    , [stages]);

    const isRemovableStage = useCallback((selected: string) =>
        !dashboards.find(dashboard => dashboard.investmentStage?.toLowerCase() === selected.toLowerCase())
    , [dashboards]);

    const handleCreateStage = useCallback(() => {
        setStages(prev => [...prev, stage]);
        stageRef.current = [...stageRef.current, stage];
        setStage('');
    }, [stage, setStages]);

    const handleEditStage = useCallback(() => {
        const promises = dashboards
            .filter(d => d.investmentStage?.trim().toLowerCase() === editStage?.trim().toLowerCase())
            .map(d => updateDashboardFunc({ ...d, investmentStage: newStage }));

        Promise.all(promises).then(() => {
            const updatedDashboards = produce(dashboards, draft => draft.forEach(dashboard => {
                if (dashboard.investmentStage?.trim().toLowerCase() === editStage?.trim().toLowerCase()) {
                    dashboard.investmentStage = newStage;
            }}));

            stageRef.current = stageRef.current.map(stage => stage === editStage ? newStage : stage);
            setStages(stageRef.current);
            setDashboards(updatedDashboards);
            setEditStage(null);
            setNewStage('');
        });
    }, [dashboards, editStage, newStage, setDashboards, setStages]);

    const handleRemoveStage = useCallback((selected: string) => {
        setStages(prev => prev.filter(stage => stage.toLowerCase() !== selected.toLowerCase()));
        stageRef.current = stageRef.current.filter(stage => stage.toLowerCase() !== selected.toLowerCase());
        setStage('');
    }, [setStages]);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Stage className={classes.headerIcon} />
                    <span>{'Manage Stages'}</span>
                </Stack>
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogContent className={classes.dialogContent}>
                <Typography fontFamily="Inter" fontSize="1rem" color={theme.colors.neutral['600']}>
                    {'Add and edit Stages'}
                </Typography>
                <OutlinedInput type="text"
                    className={classes.input}
                    placeholder="Add new stage..."
                    onChange={(e) => setStage(e.target.value)}
                    fullWidth
                    endAdornment={!!stage.trim() && (
                        <InputAdornment position="end" onClick={handleCreateStage}>
                            <Typography className={classes.addStage}>Add</Typography>
                        </InputAdornment>
                    )} />
                <Stack direction="column" className={classes.list} alignItems="flex-start">
                    {stageRef.current.map(stage => (
                        <Stack direction="row" alignItems="center" justifyContent="space-between"
                            style={{ width: "100%", marginTop: 16 }}>
                            <Stack direction="row" spacing={2} alignItems="center" width="85%">
                                {(editStage === stage) ? (
                                    <OutlinedInput type="text"
                                        className={classes.input}
                                        placeholder="Add new bucket"
                                        defaultValue={editStage}
                                        onChange={(e) => setNewStage(e.target.value)}
                                        fullWidth
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <Button variant="text"
                                                    className={classes.saveStage}
                                                    onClick={handleEditStage}
                                                    disabled={!newStage.trim() || isExistingStage(newStage)}
                                                    disableRipple> {'Save'} </Button>
                                            </InputAdornment>
                                        } />
                                ) : (
                                    <Typography className={classes.badge}>
                                        {stage}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                {(editStage !== stage) ? (
                                    <IconButton size="small"
                                        className={classes.icon}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditStage(stage);
                                            setNewStage(stage);
                                        }} disableRipple>
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                ) : (
                                    <IconButton size="small"
                                        className={classes.icon}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditStage(null);
                                            setNewStage('');
                                        }} disableRipple>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {isRemovableStage(stage) ? (
                                    <IconButton size="small"
                                        className={classes.icon}
                                        onClick={() => {
                                            setDeletableStage(stage);
                                            setDialogOpen(true);
                                        }} disabled={!!editStage}
                                        disableRipple>
                                        <DeleteIcon sx={!!editStage ? { "&.MuiSvgIcon-root": {fill: 'lightgray'} } : {}} />
                                    </IconButton>
                                ) : (
                                    <Tooltip title="Please move all associated dashboards to other stage before deleting this stage.">
                                        <DeleteIcon sx={{ "&.MuiSvgIcon-root": {fill: 'lightgray'} }} />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="contained" className={classes.cancelBtn} onClick={onClose}> {'Close'} </Button>
            </DialogActions>
        </Dialog>
        <ConfirmDialog open={dialogOpen}
            title="Delete confirmation"
            content={`Are you sure you want to delete this bucket?`}
            confirmCallback={() => {
                handleRemoveStage(deletableStage);
                setDeletableStage('');
                setDialogOpen(false);
            }}
            cancelCallback={() => setDialogOpen(false)}
        />
    </>);
}

export default ManageStagesModal;
